import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { ThemeToggle } from '@/components/ThemeToggle';
import { BrandMark } from '@/components/BrandMark';
import { cn } from '@/lib/utils';

/**
 * The chrome every signed-out screen shares: theme, language, and the mark.
 *
 * Extracted from `Login` when `/register` and `/forgot-password` arrived, rather
 * than copied into each — three signed-out pages that drift apart is how a
 * product ends up with two different logos and one page that cannot switch to
 * Arabic. The language switcher in particular has to be on ALL of them: someone
 * arriving at the register form in Arabic has no signed-in preference yet, so
 * this header is their only way to change it.
 *
 * `width` is the one thing that differs. Login is two fields and belongs in a
 * narrow column; registration is six and would be a canyon at that width.
 */
export function AuthShell({
  width = 'narrow',
  children,
}: {
  width?: 'narrow' | 'wide';
  children: ReactNode;
}) {
  const { t } = useTranslation();

  return (
    // `min-h-dvh` with `items-start` rather than `place-items-center`: the
    // register card is taller than a 320×568 viewport, and a vertically centred
    // block that overflows clips its own top edge with nothing to scroll to.
    <div className="flex min-h-dvh justify-center bg-page p-4 sm:items-center sm:py-10">
      <div className={cn('w-full space-y-4', width === 'wide' ? 'max-w-lg' : 'max-w-sm')}>
        <div className="flex items-center justify-end gap-1">
          <ThemeToggle />
          <LanguageSwitcher />
        </div>

        {/* The first thing anyone sees of this product. 64px is above the
            48px threshold at which `tarabeezaMark` keeps the tabletop ring, so
            this is the one place the mark is drawn in full. */}
        <div className="flex flex-col items-center gap-3 pb-2 text-center">
          <BrandMark size={64} />
          <div className="space-y-0.5">
            {/*
              `rtl:tracking-normal` is not decoration, it is a correctness fix.
              `brand.name` is "Tarabeeza" in English and "ترابيزة" in Arabic — the
              SAME element, so the `tracking-tight` this heading has always
              carried was applying -0.6px of letter-spacing to Arabic. Arabic is
              cursive: spacing its letters pulls apart the joins between them, so
              the word renders as disconnected shapes. Measured on this page at
              320px in `ar` before the variant was added.
            */}
            <h1 className="text-2xl font-semibold tracking-tight rtl:tracking-normal">
              {t('brand.name')}
            </h1>
            {/* Skipped when it would just repeat the line above — in Arabic both
                keys are "ترابيزة", and the lockup is bilingual, not doubled. */}
            {t('brand.nameArabic') !== t('brand.name') ? (
              <p className="font-arabic text-sm text-ink-muted" dir="rtl" lang="ar">
                {t('brand.nameArabic')}
              </p>
            ) : null}
          </div>
        </div>

        {children}
      </div>
    </div>
  );
}
